import { faUserSecret } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Dialog, DialogContent, Grid, Pagination, SxProps, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { APP_PAGE_SIZE_DEFAULT } from "app/appConstants";
import { AppGlobalClassKeys } from "app/appGlobalStyles";
import { MprcDialogTitleClose, MprcLink, MprcRequestErrorReport, MprcSpinnerIndicator } from "components";
import { MprcFilterEmptyDataset } from "components/filter/MprcFilterEmptyDataset";
import { MprcContainer } from "components/MprcContainer";
import { MprcContainerHCF } from "components/MprcContainerHCF";
import { AuditLogDtoPaginatedList, AuditLogEntityType, AuditLogRecord } from "generated/CoreApi_v1";
import { Formaters } from "lib/Formaters";
import LibUtils from "lib/LibUtils";
import { isCanceledError, useCoreApi } from "providers/CoreApiProvider";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BaseComponentHeader } from "views/common/components/BaseComponentHeader";
import { AuditLogDetail } from "views/foundation/auditLog/Home/AuditLogDetail";

////////////////////////////////////////////////////////////////////////////////

const sxHeader: SxProps = {
    textTransform: "uppercase"
};

const sxLine: SxProps = {
    border: "none",
    verticalAlign: "top"
};

interface IPros {
    title?: string;
    entityType: AuditLogEntityType;
    entityKey?: string[] | null;
    appendSecondColumnInfo?: (log: AuditLogRecord) => React.ReactNode;
    onClose: () => void;
}

/////////////////////////////////////////////////////////////////////////////////

export const AuditLogDialog: React.FC<IPros> = (props) => {
    const { title, entityType, entityKey = null, appendSecondColumnInfo, onClose } = props;
    const { t } = useTranslation();

    const api = useCoreApi();

    const [loading, setLoading] = useState<boolean>(true);
    const [viewError, setViewError] = useState<any>();
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [viewData, setViewData] = useState<AuditLogDtoPaginatedList | null>(null);
    const [detailId, setDetailId] = useState<string | null>(null);

    // Ao carregar dados...
    async function getData() {
        try {
            setLoading(true);

            var result = await api.v1.auditLog.getLogs(
                {
                    pageNumber: pageNumber,
                    pageSize: APP_PAGE_SIZE_DEFAULT,
                    deletedOnly: false,
                    entityKey,
                    entityType,
                    searchText: null,
                    eventId: null,
                    ipAddress: null
                }
            );
            setViewData(result.data);
            setViewError(null);
        }
        catch (e) {
            if (!isCanceledError(e)) {
                setViewError(e);
                setViewData(null);
            }
        }
        finally {
            setLoading(false);
        }
    }

    ////////////////////////////////////////////////////////////////////////////
    /// EFFECT's
    ////////////////////////////////////////////////////////////////////////////

    ///
    useEffect(() => {
        getData();
    }, [pageNumber]);

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    ///
    const renderDetailHeader = () => {
        return (
            <MprcContainer>
                <BaseComponentHeader
                    title={t("AuditLog.Detail.Title")}
                    onBack={() => setDetailId(null)}
                />
            </MprcContainer>
        );
    };

    ///
    const renderDetail = () => {
        return (
            <AuditLogDetail id={detailId!} />
        );
    };

    ///
    const renderList = () => {
        let oldDate: Date = new Date();
        let oldName: string = "";
        let oldSpy: string | null = null;
        let show = false;

        return (
            <TableContainer>
                <Table width="100%">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={sxHeader}>{t("AuditLog.DateText")}</TableCell>
                            <TableCell sx={sxHeader}>{t("AuditLog.CreatedByText")}</TableCell>
                            <TableCell sx={sxHeader}>{t("AuditLog.EventText")}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {
                            viewData?.items.map((record, index) => {

                                if (!LibUtils.isEqualDateMinutes(oldDate, record.createdOn) || oldName != record.creatorName || oldSpy != record.spyId) {
                                    oldDate = record.createdOn;
                                    oldName = record.creatorName;
                                    oldSpy = record.spyId;
                                    show = true;
                                }
                                else {
                                    show = false;
                                }

                                return (
                                    <TableRow key={index} className={AppGlobalClassKeys.lineHighlight}>
                                        <TableCell sx={sxLine}>
                                            {
                                                show
                                                    ? <>
                                                        {Formaters.dayMonth(record.createdOn)}
                                                        <br />
                                                        {Formaters.localeTime(record.createdOn)}
                                                    </>
                                                    : <span>&nbsp;</span>
                                            }
                                        </TableCell>

                                        <TableCell sx={sxLine}>
                                            {
                                                !show
                                                    ? <span>&nbsp;</span>
                                                    : (
                                                        <div>
                                                            {record.spyId ? <i>{record.creatorName}</i> : record.creatorName}
                                                            {record.spyId && <div><FontAwesomeIcon icon={faUserSecret} /> {record.spyName}</div>}
                                                            {appendSecondColumnInfo && appendSecondColumnInfo(record)}
                                                        </div>
                                                    )
                                            }
                                        </TableCell>

                                        <TableCell sx={sxLine}>
                                            <b>{record.eventIdName}</b>
                                            <div>
                                                {
                                                    record.referenceDetailed
                                                        ? <MprcLink onClick={() => setDetailId(record.id)}>{t("AuditLog.MoreInfoText")}</MprcLink>
                                                        : record.reference
                                                }
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        }
                    </TableBody>
                </Table>
            </TableContainer >
        );
    };

    ///
    const renderListFooter = () => {
        return (viewData?.totalPages ?? 0) > 1 &&
            <Grid sx={{ mt: (theme) => theme.spacing(2) }} alignSelf="center">
                <Pagination
                    shape="rounded"
                    color="primary"
                    page={viewData!.pageNumber}
                    count={viewData!.totalPages}
                    onChange={(e, pageNumber) => setPageNumber(pageNumber)}
                />
            </Grid>;
    };

    ///
    return (
        <Dialog
            open
            fullWidth
            maxWidth="lg"
            onClose={
                (e, reason) => {
                    if (reason == "escapeKeyDown" && detailId != null) setDetailId(null);
                    else onClose();
                }
            }
        >
            <MprcDialogTitleClose
                shrinkPadding
                title={t("AuditLog.Dialog.Title")}
                subtitle={title}
                onClose={() => onClose()}
            />

            <DialogContent sx={{ height: "100vh" }}>
                <MprcContainerHCF
                    header={
                        detailId != null
                            ? <Box sx={(theme) => ({ mt: theme.spacing(2), ml: theme.spacing(1), mr: theme.spacing(1) })}>
                                {renderDetailHeader()}
                            </Box>
                            : null
                    }
                    content={
                        <>
                            <MprcRequestErrorReport error={viewError} />
                            {
                                loading
                                    ? <MprcSpinnerIndicator />
                                    : viewData && viewData.items.length === 0
                                        ? <MprcFilterEmptyDataset message={t("AuditLog.NoRecordsFoundMessage")} />
                                        : detailId != null
                                            ? renderDetail()
                                            : renderList()
                            }
                        </>
                    }
                    footer={
                        detailId != null
                            ? null
                            : renderListFooter()
                    }
                />
            </DialogContent>
        </Dialog>
    );
};
